'use strict';
const axios = require('axios'),
  https = require('https'),
  qs = require('qs'),
  { gql } = require('graphql-tag'),
  { REACT_APP_ENV, VITE_ENV, GQL_SEC, HDB_EVT_SEC, TKN_KEY, LYFT_PASS, LYFT_RFSH, LYFT_USER, UB_SEC, UB_ID } =
    process.env,
  ENV = REACT_APP_ENV || VITE_ENV,
  REACT_APP_TKN_SD = ENV === 'production' ? 'dispatch-api-prod' : 'dispatch-api-test',
  REACT_APP_GQL_SD = ENV === 'production' ? 'gql-alb' : 'gql-test',
  LYFT_URI = ENV === 'production' ? 'api.lyft.com' : 'api-sandbox.lyft.net',
  UB_URI = ENV === 'production' ? 'api' : 'sandbox-api',
  LYFT_SCOPE = 'public offline rides.read privileged.rides.dispatch',
  UB_PRODUCT = '6a8e56b8-914e-4b48-a387-e6ad21d9c00c',
  UB_SCOPE = 'guests.trips';

const { v4: uuidv4 } = require('uuid');
const { getPropValue } = require('../utilities');

var _parent = {};

var ridehail = {
  init: p => (_parent = p),

  // -------------------------------------- VARIABLES --------------------------------------
  rideTypes: Object.freeze({
    UBER: {
      value: 'uber',
      name: 'Uber',
      code: 'uber',
      desc: 'Uber Ride Vendor',
    },
    LYFT: {
      value: 'lyft',
      name: 'Lyft',
      code: 'lyft',
      desc: 'Lyft Ride Vendor',
    },
    AUTO: {
      value: 'auto',
      name: 'Auto',
      code: 'auto',
      desc: 'Auto-Select Ride Vendor',
    },
  }),

  rideStatuses: Object.freeze({
    ESTIMATING: {
      value: 'estimating',
      name: 'Estimating',
      code: 'estimating',
      desc: 'This ride is currently gathering estimate data from ride vendors and saving it to corresponding ride attempts.',
    },
    DECIDING: {
      value: 'deciding',
      name: 'Deciding',
      code: 'deciding',
      desc: 'This ride is determining which ride vendor to utilize.',
    },
    CALLING: {
      value: 'calling',
      name: 'Calling',
      code: 'calling',
      desc: 'This ride has a specified ride vendor and is in the process of requesting the ride from the vendor',
    },
    CALLED: {
      value: 'called',
      name: 'Called',
      code: 'called',
      desc: 'This ride has been successfully requested ',
    },
    FAILED: {
      value: 'failed',
      name: 'Failed',
      code: 'failed',
      desc: 'An error has occurred in determining a ride vendor or calling this ride.',
    },
  }),

  attemptStatuses: Object.freeze({
    ESTIMATING: {
      value: 'estimating',
      name: 'Estimating',
      code: 'estimating',
      desc: 'This attempt is currently gathering and saving estimate data.',
    },
    ESTIMATED: {
      value: 'estimated',
      name: 'Estimated',
      code: 'estimated',
      desc: 'Estimate data has been successfully gathered and saved to this ride attempt.',
    },
    CHOSEN: {
      value: 'chosen',
      name: 'Chosen',
      code: 'chosen',
      desc: "This ride attempt's estimate data was compared to attempts for each ride vendor and was chosen to be called.",
    },
    NOTCHOSEN: {
      value: 'notChosen',
      name: 'Not Chosen',
      code: 'notChosen',
      desc: "This ride attempt's estimate data was compared to attempts for each ride vendor and was not chosen.",
    },
    AWAITINGRESPONSE: {
      value: 'awaitingResponse',
      name: 'Awaiting Response',
      code: 'awaitingResponse',
      desc: 'This ride attempt has been requested and is awaiting further status updates from the ride vendor.',
    },
  }),

  // -------------------------------------- RIDE CALL FUNCTIONS --------------------------------------

  /**
   * Change ride status on ride record to advance ride hail to next steps
   *
   * @param  {Number} rideId - ID of ride record
   * @param  {Number} newStatus - Status to be saved to ride record
   * @returns {object} A graphql mutation response
   */
  updateRideStatus: async (rideId, newStatus) => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }
    if (!newStatus) {
      throw new Error('no status entered');
    }

    try {
      const res = await _parent.gql.mutation(
        `
      mutation update_ride_status($rideId: bigint!, $newStatus: String!) {
        update_lyftrides(where: {id: {_eq: $rideId}}, _set: { status: $newStatus, updatedat: "now()" }) {
          affected_rows
          returning {
            id
            status
            updatedat
          }
        }
      }
    `,
        { rideId: rideId, newStatus: newStatus }
      );

      if (!res.success) throw new Error(`No response from updateRideStatus`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   * Retrieve ride move info for ride call from GraphQL server using Ride ID
   *
   * @param  {Number} rideMoveId - ID of ride move
   * @returns {Ride-Type Move} - A graphql mutation response object containing Ride move data
   */
  getRideMove: async rideMoveId => {
    if (!rideMoveId) {
      throw new Error('no ride move ID entered');
    }
    if (typeof rideMoveId !== 'number') {
      throw new Error('ride move ID must be an integer');
    }
    try {
      const res = await _parent.gql.query(
        gql`
          query findRideMove($id: bigint!) {
            moves(where: { id: { _eq: $id } }) {
              id
              customer_id
              driver_id
              driver_name
              ride_type
              customer {
                config
              }
              lane {
                id
                pickup {
                  id
                  address
                  latitude
                  longitude
                  name
                }
                delivery {
                  id
                  address
                  latitude
                  longitude
                  name
                }
              }
              ride {
                id
                active_attempt
                latitude
                longitude
                passenger_phone
                ride_move_id
                status
                active_estimate_attempts
                activeAttempt {
                  id
                  ride_id
                  status
                }
                attempts(order_by: { createdat: desc_nulls_last }) {
                  id
                  ride_id
                  ride_type
                  estimated_ride_cost
                  estimate_id
                  status
                }
              }
            }
          }
        `,
        { id: rideMoveId }
      );

      if (!res.success) throw new Error(`No response from findRideMove`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },
  /**
   * Create a new lyftrideattempts entry
   *
   * @param  {Number} rideId - ID of Ride Record
   * @param  {String} hailService - Ride Hail Service (ride type)
   * @param  {String} status - Initial status for attempt
   * @param  {String} estimateId - ID to associate together attempts containing estimate data for the same ride through different ride vendors - defaults to null
   *
   * @returns {object} A graphql mutation response
   */
  createAttempt: async (rideId, hailService, status, estimateId = null) => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (!hailService) {
      throw new Error('no ride hail service entered');
    }
    if (!status) {
      throw new Error('no initial ride attempt status entered');
    }

    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }

    try {
      const res = await _parent.gql.mutation(
        `mutation newLyftRideAttempt($rideId: bigint!, $rideType: String!, $status: String!, $estimateId: String) {
          insert_lyftrideattempts(objects: {ride_id: $rideId, ride_type: $rideType, status: $status, estimate_id: $estimateId}) {
            affected_rows
            returning {
              id
              createdat
              ride_id
              ride_type
              status
              estimate_id
              updatedat
            }
          }
        }`,
        { rideId: rideId, rideType: hailService, status: status, estimateId: estimateId }
      );

      if (!res.success) {
        console.error(`Error:`, res);
        throw new Error(`No response from newLyftRideAttempt`);
      }

      return res;
    } catch (err) {
      throw err;
    }
  },
  /**
   *  Update the active_attempt field on lyftrides to the returned ID
   *
   * @param  {Number} rideMoveId - ID of Ride-Type Move
   * @param  {Number} attemptId - ID of Ride Attempt
   * @returns {object} A graphql mutation response
   */

  updateActiveAttempt: async (rideMoveId, attemptId) => {
    if (!rideMoveId) {
      throw new Error('no ride move ID entered');
    }
    if (!attemptId) {
      throw new Error('no attempt ID entered');
    }
    if (typeof rideMoveId !== 'number') {
      throw new Error('ride move ID must be an integer');
    }
    if (typeof attemptId !== 'number') {
      throw new Error('attempt ID must be an integer');
    }
    try {
      const res = await _parent.gql.mutation(
        `mutation updateActiveAttempt(
        $rideMoveId: bigint!
        $attemptId: bigint!
      ) {
        update_lyftrides(where: {ride_move_id: {_eq: $rideMoveId}}, _set: {active_attempt: $attemptId, updatedat: "now()"}) {
          affected_rows
          returning {
            id
            ride_move_id
            active_attempt
          }
        }
      }`,
        { rideMoveId: rideMoveId, attemptId: attemptId }
      );
      if (!res.success) throw new Error(`No response from updateActiveAttempt`, res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   *  Update the active_estimate_attempts field on lyftrides to the estimate ID of current estimate attempt records
   *
   * @param  {Number} rideId - ID of Ride-Type Move
   * @param  {String} estimateId - ID to associate together attempts containing estimate data for the same ride through different ride vendors
   * @returns {object} A graphql mutation response
   */

  updateActiveEstimateAttempts: async (rideId, estimateId) => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (!estimateId) {
      throw new Error('no estimate attempt ID entered');
    }
    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }

    try {
      const res = await _parent.gql.mutation(
        `mutation updateActiveEstimateAttempts(
        $rideId: bigint!
        $estimateId: String!
      ) {
        update_lyftrides(where: {id: {_eq: $rideId}}, _set: {active_estimate_attempts: $estimateId, updatedat: "now()"}) {
          affected_rows
          returning {
            id
            ride_move_id
            active_estimate_attempts
          }
        }
      }`,
        { rideId: rideId, estimateId: estimateId }
      );
      if (!res.success) throw new Error(`No response from updateActiveEstimateAttempts`, res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   *  Clears the active_estimate_attempts field
   *
   * @param  {Number} rideId - ID of Ride-Type Move
   * @returns {object} A graphql mutation response
   */

  setActiveEstimateAttemptsNull: async rideId => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }

    try {
      const res = await _parent.gql.mutation(
        `mutation updateActiveEstimateAttempts(
          $rideId: bigint!
          $estimateId: String!
        ) {
          update_lyftrides(where: {id: {_eq: $rideId}}, _set: {active_estimate_attempts: null, updatedat: "now()"}) {
            affected_rows
            returning {
              id
              ride_move_id
              active_estimate_attempts
            }
          }
        }`,
        { rideId: rideId }
      );
      if (!res.success) throw new Error(`No response from setActiveEstimateAttemptsNull`, res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   * Check the passenger's phone number and the pickup latitude and longitude, and override them if necessary
   *
   * @param  {Object} request - Data from lyftrides_update Hasura event
   * @param  {Object} rideMove - Ride-Type Move data
   * @returns {Object} - phone number and lat/log data to override current Ride Move data
   */

  rideMoveOverrides: async (request, rideMove) => {
    let overrides;

    // Use the passenger_phone record if one is present on the Hasura event (Ride Record), otherise retrieve it from Tookan
    try {
      if (request.passenger_phone && request.passenger_phone.trim().length > 0) {
        console.log('Overriding phone number from request');
        overrides = { ...overrides, driver_phone: request.passenger_phone };
      } else {
        // Get driver phone number from Hasura
        let driverPhoneRes = await _parent.gql.query(
          `query getDriverPhone($driverId: bigint!){
          drivers(where: {id: {_eq: $driverId}}){
            user {
              phone
            }
          }
        }`,
          { driverId: rideMove.driver_id }
        );

        /** Format a phone number string to be a flat string with an international code */
        const formatPhoneFlat = (input = null) => {
          // Parse input
          if (input) {
            let cleaned = (`` + input).replace(/\D/g, ``);
            let match = cleaned.match(/^([0-9]+|)?(\d{3})(\d{3})(\d{4})$/);
            if (match) {
              return [`+`, match[1] || `1`, match[2], match[3], match[4]].join(``);
            }
          }

          // Fallback to returning the input
          return input;
        };
        let driverPhoneRaw =
          driverPhoneRes && driverPhoneRes.data && driverPhoneRes.data[0] && driverPhoneRes.data[0].user
            ? driverPhoneRes.data[0].user.phone
            : '';

        let driverPhone = formatPhoneFlat(driverPhoneRaw);

        if (driverPhone) {
          overrides = { ...overrides, driver_phone: driverPhone };
        } else {
          throw new Error('No driver phone number in Hasura driver profile response');
        }
      }

      // Use the lat/lng overrides from the request object if present, otherwise use the coordinates from the known pickup location object
      if (request.latitude && request.longitude) {
        overrides = {
          ...overrides,
          pickupLatitude: request.latitude,
          pickupLongitude: request.longitude,
        };
      }

      if (!overrides.driver_phone) throw new Error(`No driver phone returned from rideMoveOverrides`);
      return overrides;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Checks for a '+' at the start of the driver's phone number which is needed by Rideshare APIs
   *
   * @param  {String} number - Driver's phone number
   * @returns {String} - Driver's phone number (beginning with '+')
   */
  formatDriverPhone: async number => {
    if (!number) {
      throw new Error('no phone number entered');
    }

    if (number[0] !== '+') {
      console.log('Returning:', '+' + number);
      return '+' + number;
    } else {
      console.log('Returning:', number);
      return number;
    }
  },

  /**
   * Retrieve driver's name from Ride Move for ride call
   *
   * @param  {Object} rideMove - Ride-Type Move data
   * @returns {Object} - Object containing Driver's full name and seperated first and last name
   */
  getDriverName: async rideMove => {
    if (!rideMove.driver_name) {
      throw new Error('no driver name entered');
    }

    let fullName = 'HopDriver';

    if (rideMove.driver_name && rideMove.driver_name !== undefined && rideMove.driver_name.length > 0) {
      fullName = rideMove.driver_name.split(' ');
    }

    return {
      fullName: fullName,
      first: fullName[0],
      last: fullName[fullName.length - 1],
    };
  },

  /**
   * Returns true if driver's name will be accepted by Rideshare APIs, else returns false
   *
   * @param  {String} firstName - Driver's first name
   * @param  {String} lastName - Driver's last name
   * @returns {Boolean} - True if driver's name is not null, undefined, or length < 1
   */
  validateDriverName: async (firstName, lastName) => {
    if (
      !firstName ||
      !lastName ||
      firstName === null ||
      lastName === null ||
      firstName === undefined ||
      lastName === undefined ||
      firstName.trim().length < 1 ||
      lastName.trim().length < 1
    ) {
      return false;
    } else {
      return true;
    }
  },

  /**
   * Update Ride Attempt Record with ride data from buildUberAttemptUpdateObject or buildLyftAttemptUpdateObject
   *
   * @param  {Object} dataObject - Data object returned from buildAttemptUpdateObject
   *
   * @returns {Object} - A graphql mutation response object
   */
  updateRideAttempt: async dataObject => {
    try {
      const res = await _parent.gql.mutation(
        `mutation updateLyftRideAttempt(
            $id: bigint!
            $rideshare_id: String
            $status: String!
            $estimated_ride_cost: numeric
            $surge_cost_percentage: String
            $estimated_cost_min: numeric
            $estimated_cost_max: numeric
            $origin_location_id: bigint
            $destination_location_id: bigint
            $rideshare_pickup_latitude: numeric
            $rideshare_pickup_longitude: numeric
            $rideshare_pickup_address: String
            $rideshare_delivery_latitude: numeric
            $rideshare_delivery_longitude: numeric
            $rideshare_delivery_address: String
            $stop_id: bigint  
          ) {
            update_lyftrideattempts(
              where: {id: {_eq: $id}},
              _set: {
                status: $status
                rideshare_id: $rideshare_id
                estimated_ride_cost: $estimated_ride_cost
                surge_cost_percentage: $surge_cost_percentage
                estimated_cost_min: $estimated_cost_min
                estimated_cost_max: $estimated_cost_max
                origin_location_id: $origin_location_id
                destination_location_id: $destination_location_id
                rideshare_pickup_latitude: $rideshare_pickup_latitude
                rideshare_pickup_longitude: $rideshare_pickup_longitude
                rideshare_pickup_address: $rideshare_pickup_address
                rideshare_delivery_latitude: $rideshare_delivery_latitude
                rideshare_delivery_longitude: $rideshare_delivery_longitude
                rideshare_delivery_address: $rideshare_delivery_address
                stop_id: $stop_id
                updatedat: "now()"
            }) {
              affected_rows
            }
          }`,
        dataObject
      );
      if (!res.success) throw new Error(`No response from updateRideAttempt`, res);
      return res;
    } catch (err) {
      throw err;
    }
  },
  /**
   * Update Ride Attempt Record with ride data from buildUberAttemptUpdateObject or buildLyftAttemptUpdateObject
   * @param  {Number} attemptId - ID of Ride Attempt
   * @param  {String} status - New attempt status
   * @param  {Object} dataObject - Data object returned from buildAttemptUpdateObject
   *
   * @returns {Object} - A graphql mutation response object
   */
  updateAttemptStatus: async (attemptID, status) => {
    try {
      const res = await _parent.gql.mutation(
        `mutation updateAttemptStatus($id: bigint!, $status: String!) {
          update_lyftrideattempts(where: {id: {_eq: $id}}, _set: {status: $status, updatedat: "now()"}) {
            affected_rows
            returning {
              id
              status
              updatedat
            }
          }
        }`,
        { id: attemptID, status: status }
      );
      if (!res.success) throw new Error(`No response from updateAttemptStatus`, res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   * Resets active attempt if Ride call fails after a new attempt record is created and set to active.
   *
   * @param  {Number} rideMoveId - ID of Ride-Type Move
   * @param  {Number} activeAttemptId - ID of active attempt record
   */

  updateRideFailure: async (rideMoveId, activeAttemptId) => {
    let msg;

    try {
      const res = await _parent.gql.mutation(
        gql`
          mutation sdk_updateRideFailure($rideMoveId: bigint!, $attemptId: bigint!) {
            update_lyftrideattempts(
              where: { id: { _eq: $attemptId } }
              _set: { status: "failed", updatedat: "now()" }
            ) {
              affected_rows
              returning {
                id
                ride_id
                status
                updatedat
              }
            }
            update_lyftrides(
              where: { ride_move_id: { _eq: $rideMoveId } }
              _set: { active_attempt: null, active_estimate_attempts: null, status: "failed", updatedat: "now()" }
            ) {
              affected_rows
              returning {
                id
                ride_move_id
                active_attempt
                active_estimate_attempts
                status
                updatedat
              }
            }
            update_moves(where: { id: { _eq: $rideMoveId } }, _set: { status: "failed", updatedat: "now()" }) {
              affected_rows
              returning {
                id
                status
                updatedat
              }
            }
          }
        `,
        { rideMoveId: rideMoveId, attemptId: activeAttemptId }
      );
      if (res.success) {
        msg = 'Ride call failure - attempt record set to failed and ride record active_attempt set to null.';
        console.log(msg, res.data.data);
      } else {
        msg = 'Failed to update attempt record and set active_attempt to null on ride record.';
        console.error(msg);
      }
    } catch (err) {
      msg = 'Error updating attempt record and setting active_attempt to null on ride record.';
      console.error(msg, err);
    }
  },

  /**
   * Sets ride move to failed if an active attempt was never created
   *
   * @param  {Number} rideMoveId - ID of Ride-Type Move
   */

  updateRideMoveFailure: async rideMoveId => {
    let msg;

    try {
      const res = await _parent.gql.mutation(
        gql`
          mutation sdk_updateRideMoveFailure($rideMoveId: bigint!) {
            update_moves(where: { id: { _eq: $rideMoveId } }, _set: { status: "failed", updatedat: "now()" }) {
              affected_rows
              returning {
                id
                status
                updatedat
              }
            }
          }
        `,
        { rideMoveId: rideMoveId }
      );
      if (res.success) {
        msg = 'Ride call failure - ride move record set to failed.';
        console.log(msg, res.data.data);
      } else {
        msg = 'Failed to update ride move record.';
        console.error(msg);
      }
    } catch (err) {
      msg = 'Error updating ride move record.';
      console.error(msg, err);
    }
  },

  /**
   * Resets active estimate attempts if Ride call fails after new estimate attempt records are created and set to active.
   *
   * @param  {Number} rideId - ID of Ride-Type Move
   */

  setActiveEstimateAttemptsToNull: async rideId => {
    let msg;

    try {
      const res = await _parent.gql.mutation(
        gql`
          mutation updateActiveEstimateAttemptNull($rideId: bigint!) {
            update_lyftrides(
              where: { id: { _eq: $rideId } }
              _set: { active_estimate_attempts: null, updatedat: "now()" }
            ) {
              affected_rows
              returning {
                id
                ride_move_id
                active_estimate_attempts
              }
            }
          }
        `,
        { rideId: rideId }
      );
      if (res.success) {
        msg = 'Ride call failure - ride record active_estimate_attempts set to null.';
        console.log(msg, res.data.data);
      } else {
        msg = 'Failed to set active_estimate_attempts to null on ride record.';
        console.error(msg);
      }
    } catch (err) {
      msg = 'Error setting active_estimate_attempts to null on ride record.';
      console.error(msg, err);
    }
  },

  // -------------------------------------- LYFT FUNCTIONS --------------------------------------

  /**
   * Send request to Lyft's API with credentials to retrieve an access token
   *
   * @param {Object} existingAuthStub - Existing token stub that includes access_token, expires_in, token_type, and scope
   * @param {Number} existingExpiration - Expiration date of existing token stub
   * @returns {Object} - Stub containing access token from Lyft API
   */
  authorizeLyft: async (existingAuthStub = null, existingExpiration = null) => {
    // If an existing token stub exists, check the expiration date and return if it's still valid
    // If the expiration is less than 5 minutes from now, request a new token
    if (existingAuthStub && existingAuthStub.access_token) {
      if (existingExpiration && existingExpiration > Date.now() + 1000 * 60 * 5) {
        console.log('Existing Lyft Auth Response Found:', existingAuthStub);
        return { authStub: existingAuthStub, expiration: existingExpiration };
      } else {
        console.log('Existing Lyft Auth Response Expired:', existingAuthStub);
      }
    }

    // If no existing token stub exists, request a new one
    let tokenStub = {
      access_token: '',
      expires_in: 3600,
      token_type: 'Bearer',
      scope: '',
    };

    //HOTFIX FOR CERT ERR
    const agent = new https.Agent({
      rejectUnauthorized: false,
    });

    try {
      await axios
        .post(
          `https://${LYFT_URI}/oauth/token`,
          { grant_type: 'client_credentials', scope: LYFT_SCOPE },
          {
            'Content-Type': 'application/json',
            auth: {
              username: LYFT_USER,
              password: LYFT_PASS,
            },
            //HOTFIX FOR CERT ERR
            httpsAgent: agent,
          }
        )
        .then(res => {
          if (res && res.data) {
            let response = res.data;
            console.log('Lyft Auth Response:', response);
            tokenStub = {
              access_token: response.access_token ? response.access_token : '',
              expires_in: response.expires_in ? response.expires_in : 3600,
              token_type: response.token_type ? response.token_type : 'Bearer',
              scope: response.scope ? response.scope : '',
            };
          } else {
            throw new Error("Failed to request an access token from Lyft's API", res);
          }
        });
    } catch (err) {
      throw err;
    }
    if (!tokenStub.access_token) throw new Error("No access token returned from Lyft's API");
    return { authStub: tokenStub, expiration: Date.now() + tokenStub.expires_in * 1000 };
  },

  /**
   * Build header for subsequent Lyft API calls
   *
   * @param {String} lyftBearerToken - Bearer Access Token returned from Lyft API
   * @returns {Object} - Header containing access token authorization
   */

  buildLyftHeader: async lyftBearerToken => {
    if (!lyftBearerToken) {
      throw new Error('no Access Token entered');
    }

    let header = {
      'content-type': 'application/json',
      authorization: lyftBearerToken,
    };

    return header;
  },

  /**
   * Request a cost estimate for a Lyft ride
   *
   * @param  {Object} rideMove - Ride-Type Move data
   * @param  {Object} lyftHeader - Axios header containing access token from authorizeLyft API call
   * @returns {Object} - Stub containing returned data from Lyft API
   */
  estimateLyft: async (rideMove, lyftHeader) => {
    let estimateStub = {
      success: false,
      min_cost: 0,
      max_cost: 0,
      avg_cost: 0,
      surge_percent: '0%',
    };
    const rideLane = rideMove.lane;
    if (!rideLane) throw new Error('estimateLyft() failed; ride move has no lane');
    const pickupLat = getPropValue(rideLane, 'pickup.latitude');
    const pickupLong = getPropValue(rideLane, 'pickup.longitude');
    const deliveryLat = getPropValue(rideLane, 'delivery.latitude');
    const deliveryLong = getPropValue(rideLane, 'delivery.longitude');
    if (!pickupLat || !pickupLong || !deliveryLat || !deliveryLong) {
      throw new Error(
        `estimateLyft() failed: missing location latitude/longitude. Pickup: lat: ${pickupLat}, long: ${pickupLong}. Delivery: lat: ${deliveryLat}, long: ${deliveryLong}.`
      );
    }
    try {
      //HOTFIX FOR CERT ERR
      const agent = new https.Agent({
        rejectUnauthorized: false,
      });
      await axios
        .get(
          `https://${LYFT_URI}/v1/cost?start_lat=${pickupLat}&start_lng=${pickupLong}&end_lat=${deliveryLat}&end_lng=${deliveryLong}`,
          {
            headers: lyftHeader,
            //HOTFIX FOR CERT ERR
            httpsAgent: agent,
          }
        )
        .then(res => {
          // The response items in the cost_estimates array are not always in the same sequence so looping through the array to find the appropriate object
          if (res && res.data && res.data.cost_estimates.length > 0) {
            for (let index of res.data.cost_estimates) {
              if (index.ride_type && index.ride_type.toLowerCase() === 'lyft') {
                estimateStub = {
                  success: true,
                  ...index,
                };
              }
            }
          } else {
            // No response from API
            throw new Error("Failed to retrieve a price estimate from Lyft's API", res);
          }
        });
    } catch (err) {
      throw err;
    }
    if (!estimateStub.success) throw new Error("Failed to parse price estimates from Lyft's API");
    return estimateStub;
  },

  /**
   * Request a Lyft dispatch to the HopDriver's location
   *
   * @param  {Object} rideMove - Ride-Type Move data
   * @param  {Object} lyftHeader - Header containing access token returned from buildLyftHeader
   * @param  {String} driverFirstName - Driver's first name
   * @param  {String} driverLastName - Driver's last name
   * @param  {String} costToken - (Optional) token from estimate request- locks in cost estimate during "Prime Time"
   * @returns {Object} - Stub containing returned data from Lyft API
   */
  requestLyft: async (rideMove, lyftHeader, driverFirstName, driverLastName, costToken = null) => {
    let requestStub;

    //HOTFIX FOR CERT ERR
    const agent = new https.Agent({
      rejectUnauthorized: false,
    });

    // Define request variables
    let origin = {};
    let destination = {};
    let passenger = null;

    // Set origin and destination based on environment
    // Must be hardcoded in test environment because thats all lyft allows
    if (REACT_APP_ENV === `production`) {
      origin = {
        lat: rideMove.lane.pickup.latitude,
        lng: rideMove.lane.pickup.longitude,
        address: rideMove.lane.pickup.address,
      };
      destination = {
        lat: rideMove.lane.delivery.latitude,
        lng: rideMove.lane.delivery.longitude,
        address: rideMove.lane.delivery.address,
      };
      passenger = {
        first_name: driverFirstName,
        last_name: driverLastName,
        phone_number: rideMove.driver_phone,
      };
    } else {
      origin = {
        lat: 34.10439,
        lng: -118.31989,
        address: `1811 Tamarind Ave, Los Angeles, CA 90028`,
      };
      destination = {
        lat: 34.11844,
        lng: -118.30642,
        address: `2715 N Vermont Canyon Rd, Los Angeles, CA 90027`,
      };
      passenger = {
        first_name: driverFirstName,
        last_name: driverLastName,
        phone_number: `+13215550100`,
      };
    }

    try {
      await axios
        .post(
          `https://${LYFT_URI}/v1/dispatches`,
          {
            ride_type: 'lyft',
            origin: origin,
            destination: destination,
            passenger: passenger,
            ...(costToken ? { cost_token: costToken } : {}),
          },
          {
            headers: lyftHeader,
            //HOTFIX FOR CERT ERR
            httpsAgent: agent,
          }
        )
        .then(async res => {
          if (res.status !== 201) {
            throw new Error("Failed to request a dispatch from Lyft's API", res);
          } else {
            requestStub = res.data;
          }
        });
    } catch (err) {
      console.log('Failed Lyft Request Body:', {
        ride_type: 'lyft',
        origin: origin,
        destination: destination,
        passenger: passenger,
      });
      throw err;
    }
    return requestStub;
  },

  /**
   * Format data from Uber or Lyft calls into an object to be saved on the ride attempt. If no request is passed in, only estimate data will be saved for auto ride select calculations.
   *
   * @param  {Object} rideMove - Ride Move data returned from Hasura
   * @param  {Number} lyftAttemptID - ID of active attempt record
   * @param  {Object} lyftRideCostEstimate - Data returned from rideshare estimate call.
   * @param  {Object} lyftRequest - Data returned from rideshare request call.
   *
   * @returns {Object} - A graphql mutation response object
   */
  buildLyftAttemptUpdateObject: async (rideMove, lyftAttemptID, lyftRideCostEstimate, lyftRequest) => {
    let attemptUpdateObject = {
      id: lyftAttemptID,
      origin_location_id: rideMove.lane.pickup.id,
      destination_location_id: rideMove.lane.delivery.id,
      // Getting the average cost from the min and max values returned
      estimated_ride_cost:
        lyftRideCostEstimate.estimated_cost_cents_min && lyftRideCostEstimate.estimated_cost_cents_max
          ? (lyftRideCostEstimate.estimated_cost_cents_min + lyftRideCostEstimate.estimated_cost_cents_max) * 0.005
          : 0,
      surge_cost_percentage: lyftRideCostEstimate.primetime_percentage
        ? lyftRideCostEstimate.primetime_percentage
        : '0%',
      estimated_cost_min: lyftRideCostEstimate.estimated_cost_cents_min
        ? lyftRideCostEstimate.estimated_cost_cents_min * 0.01
        : 0,
      estimated_cost_max: lyftRideCostEstimate.estimated_cost_cents_max
        ? lyftRideCostEstimate.estimated_cost_cents_max * 0.01
        : 0,
    };

    if (lyftRequest) {
      console.log('Ride Request Found: Building Update Object with Estimate and Request Data');
      let requestUpdate = {
        rideshare_id: lyftRequest.ride_id,
        status: lyftRequest.status,
        rideshare_pickup_latitude: lyftRequest && lyftRequest.origin.lat ? lyftRequest.origin.lat : 0,
        rideshare_pickup_longitude: lyftRequest && lyftRequest.origin.lng ? lyftRequest.origin.lng : 0,
        rideshare_pickup_address: lyftRequest && lyftRequest.origin.address ? lyftRequest.origin.address : '',
        rideshare_delivery_latitude: lyftRequest && lyftRequest.destination.lat ? lyftRequest.destination.lat : 0,
        rideshare_delivery_longitude: lyftRequest && lyftRequest.destination.lng ? lyftRequest.destination.lng : 0,
        rideshare_delivery_address:
          lyftRequest && lyftRequest.destination.address ? lyftRequest.destination.address : '',
      };
      Object.assign(attemptUpdateObject, requestUpdate);
    } else {
      console.log('No Ride Request Found: Building Update Object with only Estimate Data');
      let estimateUpdate = {
        status: ridehail.attemptStatuses.ESTIMATED.code,
      };
      Object.assign(attemptUpdateObject, estimateUpdate);
    }
    return attemptUpdateObject;
  },

  /**
   * Send request to Lyft's API to cancel current ride
   *
   * @param  {Object} lyftHeader - Header containing access token returned from buildLyftHeader
   * @param  {String} rideshareID - Lyft ride ID saved in Attempt Record
   * @returns {Object} - Stub containing any data returned from call (Empty string returned on successful calls)
   */
  cancelLyft: async (lyftHeader, rideshareId) => {
    let cancelStub = {
      success: false,
      data: {},
    };

    // Build URL based on enviornment
    // Prod: https://api.lyft.com/v1/dispatches/{rideshareId}/cancel
    // Sandbox: https://api-sandbox.lyft.net/v1/sandbox/rides/{rideshareId}/deactivate

    let dispatch = REACT_APP_ENV === 'production' ? `dispatches/` : `sandbox/rides/`;
    let cancel = REACT_APP_ENV === 'production' ? `/cancel` : `/deactivate`;

    let url = `https://` + LYFT_URI + `/v1/` + dispatch + rideshareId + cancel;

    //HOTFIX FOR CERT ERR
    const agent = new https.Agent({
      rejectUnauthorized: false,
    });
    try {
      await axios
        .post(
          url,
          {},
          {
            headers: lyftHeader,
            //HOTFIX FOR CERT ERR
            httpsAgent: agent,
          }
        )
        .then(res => {
          if (res.status !== 204) {
            throw new Error("Failed to cancel dispatch from Lyft's API", res);
          } else {
            cancelStub = {
              success: true,
              data: res.data,
            };
          }
        });
    } catch (err) {
      throw err;
    }
    return cancelStub;
  },

  // -------------------------------------- UBER FUNCTIONS --------------------------------------

  /**
   * Send request to Uber's API with credentials to retrieve an access token
   *
   * @param {Object} existingAuthStub - Existing token stub that includes access_token, expires_in, token_type, and scope
   * @param {Number} existingExpiration - Expiration date of existing token stub
   * @returns {Object} - Stub containing access token from Uber API
   */
  authorizeUber: async (existingAuthStub = null, existingExpiration = null) => {
    // If an existing token stub exists, check the expiration date and return if it's still valid
    // If the expiration is less than 5 minutes from now, request a new token
    if (existingAuthStub && existingAuthStub.access_token) {
      if (existingExpiration && existingExpiration > Date.now() + 1000 * 60 * 5) {
        console.log('Existing Uber Auth Response Found:', existingAuthStub);
        return { authStub: existingAuthStub, expiration: existingExpiration };
      } else {
        console.log('Existing Uber Auth Response Expired:', existingAuthStub);
      }
    }

    // If no existing token stub exists, request a new one
    let tokenStub = {
      success: false,
      access_token: '',
      expires_in: 3600,
      token_type: 'Bearer',
      scope: '',
    };

    try {
      const data = qs.stringify({
        client_id: UB_ID,
        client_secret: UB_SEC,
        grant_type: 'client_credentials',
        scope: UB_SCOPE,
      });

      await axios({
        url: 'https://login.uber.com/oauth/v2/token',
        method: 'post',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: data,
      }).then(res => {
        if (res && res.data) {
          let response = res.data;
          console.log('Uber Auth Response:', response);
          tokenStub = {
            success: true,
            access_token: response.access_token ? response.access_token : '',
            expires_in: response.expires_in ? response.expires_in : 3600,
            token_type: response.token_type ? response.token_type : 'Bearer',
            scope: response.scope ? response.scope : '',
          };
        } else {
          throw new Error("Failed to request an access token from Uber's API", res);
        }
      });
    } catch (err) {
      throw err;
    }
    if (!tokenStub.access_token) throw new Error("No access token returned from Uber's API");
    return { authStub: tokenStub, expiration: Date.now() + tokenStub.expires_in * 1000 };
  },

  /**
   * Build header for subsequent Uber API calls, adds sandbox run ID if not in prod env
   *
   * @param {String} uberBearerToken - Bearer Access Token returned from Uber API
   * @param {String} sandboxRunID - Run ID returned from Uber Sandbox API (if in sandbox env, defaults to null in production)
   * @returns {Object} - Header containing access token authorization
   */
  buildUberHeader: async (uberBearerToken, sandboxRunID = null) => {
    if (!uberBearerToken) {
      throw new Error('no Access Token entered');
    }

    let header = {
      'content-type': 'application/json',
      authorization: uberBearerToken,
    };

    if (sandboxRunID) Object.assign(header, { 'x-uber-sandbox-runuuid': sandboxRunID.trim() });
    return header;
  },

  /**
   * Request a cost estimate for an Uber ride
   *
   * @param  {Object} rideMove - Ride-Type Move data
   * @param  {Object} uberHeader - Axios header containing access token from authorizeUber API call
   * @returns {Object} - Stub containing returned data from Lyft API
   */
  estimateUber: async (rideMove, uberHeader) => {
    let estimateStub = {
      success: false,
    };

    try {
      var data = JSON.stringify({
        pickup: {
          latitude: rideMove.lane.pickup.latitude,
          longitude: rideMove.lane.pickup.longitude,
        },
        dropoff: {
          latitude: rideMove.lane.delivery.latitude,
          longitude: rideMove.lane.delivery.longitude,
        },
      });

      var config = {
        method: 'post',
        url: `https://${UB_URI}.uber.com/v1/guests/trips/estimates`,
        headers: uberHeader,
        data: data,
      };

      await axios(config).then(res => {
        if (res && res.data && res.data.product_estimates.length > 0) {
          let estimates = res.data.product_estimates;
          let product = estimates.find(estimate => estimate.product.parent_product_type_id === UB_PRODUCT);
          if (!product) throw new Error("Failed to parse price estimates from Uber's API", res);

          estimateStub = {
            success: true,
            fares_unavailable: res.data.fares_unavailable,
            etas_unavailable: res.data.etas_unavailable,
            ...product,
          };
        } else throw new Error("Failed to retrieve a price estimate from Uber's API", res);
      });
    } catch (err) {
      throw err;
    }

    return estimateStub;
  },

  /**
   * Request an Uber dispatch to the HopDriver's location
   *
   * @param  {Object} rideMove - Ride-Type Move data
   * @param  {Object} uberHeader - Header containing access token returned from buildUberHeader
   * @param  {String} driverFirstName - Driver's first name
   * @param  {String} driverLastName - Driver's last name
   * @param  {Object} uberRideCostEstimate - Data returned from estimateUber call
   * @returns {Object} - Stub containing returned data from Uber API
   */
  requestUber: async (rideMove, uberHeader, driverFirstName, driverLastName, uberRideCostEstimate) => {
    let requestStub;

    try {
      await axios
        .post(
          `https://${UB_URI}.uber.com/v1/guests/trips`,
          {
            product_id: uberRideCostEstimate.product.product_id,
            guest: {
              first_name: driverFirstName,
              last_name: driverLastName,
              phone_number: rideMove.driver_phone,
            },
            start_latitude: rideMove.lane.pickup.latitude,
            start_longitude: rideMove.lane.pickup.longitude,
            end_latitude: rideMove.lane.delivery.latitude,
            end_longitude: rideMove.lane.delivery.longitude,
            contacts_to_notify: [
              {
                phone_number: rideMove.driver_phone,
                contact_event: ['TRIP_BEGAN', 'DRIVER_PICKUP_COMPLETED'],
              },
            ],
          },
          {
            headers: uberHeader,
          }
        )
        .then(async res => {
          if (res.status !== 200) {
            throw new Error("Failed to request a dispatch from Uber's API", res);
          } else {
            requestStub = res.data;
          }
        });
    } catch (err) {
      throw err;
    }

    return requestStub;
  },

  /**
   * Translate an Uber ride status into a Hopdrive (Lyft) ride status
   *
   * @param  {String} uberStatus - Status of Uber ride
   * @returns {String} - Equivalent Hopdrive ride status
   */
  translateStatus: async uberStatus => {
    if (!uberStatus) {
      throw new Error('no Uber status entered');
    }
    let translation = uberStatus;

    switch (uberStatus) {
      case 'processing':
        translation = 'pending';
        break;
      case 'arriving':
        translation = 'arrived';
        break;
      case 'in_progress':
        translation = 'pickedUp';
        break;
      case 'completed':
        translation = 'droppedOff';
        break;
      case 'driver_canceled':
        translation = 'canceled';
        break;
      case 'rider_canceled':
        translation = 'canceled';
        break;
      default:
        // 'no_drivers_available', 'accepted', 'failed', 'offered', 'expired', 'driver_redispatched'
        break;
    }
    console.log(`Uber Status: ${uberStatus} translated to Hopdrive Status: ${translation}`);
    return translation;
  },

  /**
   * Get Trip data from Uber API, containing pickup and dropoff locations
   *
   * @param  {Object} uberHeader - Header containing access token returned from buildUberHeader
   * @param  {String} requestID - Request ID returned from requestUber API call
   * @returns {Object} - Stub containing returned data from Uber API
   */
  getUberTripDetails: async (uberHeader, requestID) => {
    let tripStub;

    try {
      await axios
        .get(`https://${UB_URI}.uber.com/v1/guests/trips/${requestID}`, {
          headers: uberHeader,
        })
        .then(async res => {
          if (res && res.data) {
            tripStub = res.data;
          } else {
            throw new Error("Failed to retrieve trip data from Uber's API", res);
          }
        });
    } catch (err) {
      throw err;
    }

    return tripStub;
  },

  /**
   * Format data from Uber calls into an object to be saved on the ride attempt. If no request is passed in, only estimate data will be saved for auto ride select calculations.
   *
   * @param  {Object} rideMove - Ride Move data returned from Hasura
   * @param  {Number} uberAttemptID - ID of active attempt record
   * @param  {Object} uberRideCostEstimate - Data returned from rideshare estimate call.
   * @param  {Object} uberRequest - Data returned from rideshare request call.
   * @param  {Object} uberTrip - Data returned from rideshare get trip call (Exclusive to Uber).
   *
   * @returns {Object} - A graphql mutation response object
   */
  buildUberAttemptUpdateObject: async (rideMove, uberAttemptID, uberRideCostEstimate, uberRequest, uberTrip) => {
    let { estimate_info } = uberRideCostEstimate;
    let estimated_ride_cost, surge_cost_percentage, estimated_cost_min, estimated_cost_max;

    if (!uberRideCostEstimate.fares_unavailable) {
      if (!estimate_info.estimate && estimate_info.fare) {
        console.log('Fare, No Estimate');
        estimated_ride_cost = estimate_info.fare.value ? estimate_info.fare.value : 0;
        estimated_cost_min = estimate_info.fare.value ? estimate_info.fare.value : 0;
        estimated_cost_max = estimate_info.fare.value ? estimate_info.fare.value : 0;
        surge_cost_percentage = '0%';
      }
      if (estimate_info.estimate && !estimate_info.fare) {
        console.log('Estimate, No Fare');
        estimated_cost_min = estimate_info.estimate.low_estimate ? estimate_info.estimate.low_estimate : 0;
        estimated_cost_max = estimate_info.estimate.high_estimate ? estimate_info.estimate.high_estimate : 0;
        estimated_ride_cost =
          estimate_info.estimate.low_estimate && estimate_info.estimate.high_estimate
            ? (estimate_info.estimate.low_estimate + estimate_info.estimate.high_estimate) * 0.5
            : 0;
        surge_cost_percentage = estimate_info.estimate.surge_multiplier
          ? ((estimate_info.estimate.surge_multiplier - 1) * 100).toString() + '%'
          : '0%';
      }
    } else {
      console.log('Fares Unavailable');
      estimated_ride_cost = 0;
      surge_cost_percentage = '0%';
      estimated_cost_min = 0;
      estimated_cost_max = 0;
    }

    let attemptUpdateObject = {
      id: uberAttemptID,
      origin_location_id: rideMove.lane.pickup.id,
      destination_location_id: rideMove.lane.delivery.id,
      estimated_ride_cost: estimated_ride_cost,
      surge_cost_percentage: surge_cost_percentage,
      estimated_cost_min: estimated_cost_min,
      estimated_cost_max: estimated_cost_max,
    };
    if (uberRequest) {
      console.log('Ride Request Found: Building Update Object with Estimate, Request, and Trip Data');
      let requestUpdate = {
        rideshare_id: uberRequest.request_id,
        status: uberRequest.status,
        rideshare_pickup_latitude: uberTrip && uberTrip.pickup.latitude ? uberTrip.pickup.latitude : 0,
        rideshare_pickup_longitude: uberTrip && uberTrip.pickup.longitude ? uberTrip.pickup.longitude : 0,
        rideshare_pickup_address: uberTrip && uberTrip.pickup.address ? uberTrip.pickup.address : '',
        rideshare_delivery_latitude: uberTrip && uberTrip.destination.latitude ? uberTrip.destination.latitude : 0,
        rideshare_delivery_longitude: uberTrip && uberTrip.destination.longitude ? uberTrip.destination.longitude : 0,
        rideshare_delivery_address: uberTrip && uberTrip.destination.address ? uberTrip.destination.address : '',
      };
      Object.assign(attemptUpdateObject, requestUpdate);
    } else {
      console.log('No Ride Request Found: Building Update Object with only Estimate Data');
      let estimateUpdate = {
        status: ridehail.attemptStatuses.ESTIMATED.code,
      };
      Object.assign(attemptUpdateObject, estimateUpdate);
    }
    return attemptUpdateObject;
  },

  /**
   * Send request to Uber's API to cancel current ride
   *
   * @param  {Object} uberHeader - Header containing access token returned from buildUberHeader
   * @param  {String} rideshareID - Uber ride ID saved in Attempt Record
   * @returns {Object} - Stub containing any data returned from call (Empty string returned on successful calls)
   */
  cancelUber: async (uberHeader, rideshareId) => {
    let cancelStub = {
      success: false,
      data: {},
    };

    //HOTFIX FOR CERT ERR
    const agent = new https.Agent({
      rejectUnauthorized: false,
    });
    try {
      await axios
        .delete(`https://${UB_URI}.uber.com/v1/guests/trips/${rideshareId}`, {
          headers: uberHeader,
          //HOTFIX FOR CERT ERR
          httpsAgent: agent,
        })
        .then(res => {
          if (res.status !== 204) {
            throw new Error("Failed to cancel dispatch from Uber's API", res);
          } else {
            cancelStub = {
              success: true,
              data: res.data,
            };
          }
        });
    } catch (err) {
      throw err;
    }
    return cancelStub;
  },

  /**
   * Use URL provided by Uber webhook to get remaining event data
   *
   * @param  {Object} url - url provided by Uber Webhook event
   * @param  {String} authToken - Uber Bearer Auth token from authorizeUber
   * @returns {Object} fullPayload - Object containing payload data returned from call
   */
  getFullPayload: async (url, authToken) => {
    let response;
    try {
      const agent = new https.Agent({
        rejectUnauthorized: false,
      });
      await axios({
        url: url,
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        httpsAgent: agent,
      }).then(res => {
        if (res && res.data) {
          response = res.data;
          console.log('Full Payload successfully retrieved', res.data);
        }
      });
    } catch (err) {
      console.log('Error retrieving full payload from Uber API:', err);
    }
    return response;
  },

  /* UBER [SANDBOX-SPECIFC] */

  /**
   * Send request to Uber's API to create a Sandbox Run
   * @param {String} uberAccessToken - Bearer Access Token returned from Uber API
   * @param {Object} rideMove - Ride-Type Move data
   * @returns {Object} - Stub containing Sandbox Run ID from Uber API
   */
  createUberSandboxRun: async (uberAccessToken, rideMove) => {
    let runStub;
    try {
      var data = JSON.stringify({
        driver_locations: [
          {
            latitude: rideMove.lane.pickup.latitude,
            longitude: rideMove.lane.pickup.longitude,
          },
        ],
        pickup_location: {
          latitude: rideMove.lane.pickup.latitude,
          longitude: rideMove.lane.pickup.longitude,
        },
        dropoff_location: {
          latitude: rideMove.lane.delivery.latitude,
          longitude: rideMove.lane.delivery.longitude,
        },
        parent_product_type_id: UB_PRODUCT,
      });

      var config = {
        method: 'post',
        url: 'https://sandbox-api.uber.com/v1/guests/sandbox/run',
        headers: {
          Authorization: uberAccessToken,
          'Content-Type': 'application/json',
        },
        data: data,
      };

      await axios(config).then(res => {
        if (res && res.data) {
          let response = res.data;
          runStub = response.run_id ? response.run_id : '';
        } else {
          throw new Error('Failed to create Sandbox Run in Uber API.', res);
        }
      });
    } catch (err) {
      throw err;
    }
    return runStub;
  },

  /**
   * Send request to Uber's API to activate Sandbox Run, retrieve Sandbox Run ID, and Sandbox Driver IDs
   * @param {String} uberAccessToken - Bearer Access Token returned from Uber API
   * @param {String} sandboxRunID - Run ID returned from createSandboxRun
   * @returns {Object} - Stub containing Sandbox Run ID and Driver IDs from Uber API
   */
  getUberSandboxRunAndDriverIDs: async (uberAccessToken, sandboxRunID) => {
    let runIDStub = {
      success: false,
      run_id: '',
      driver_ids: [],
    };

    var config = {
      method: 'get',
      url: `https://sandbox-api.uber.com/v1/guests/sandbox/run/${sandboxRunID}`,
      headers: {
        'content-type': 'application/json',
        authorization: uberAccessToken,
      },
    };

    try {
      await axios(config).then(res => {
        if (res && res.data) {
          let response = res.data;
          runIDStub = {
            success: true,
            run_id: response.run_id ? response.run_id : '',
            driver_ids: response.driver_ids ? response.driver_ids : [],
          };
        } else throw new Error('Failed to retrieve Uber Sandbox Run ID + Uber Driver IDs in Uber API.', res);
      });
    } catch (err) {
      throw err;
    }
    return runIDStub;
  },

  /**
   * Send request to Uber's API to update Sandbox Driver State, moving sandbox ride through its life cycle so Uber sends webhook updates
   * @param  {Object} uberHeader - Header containing access token and sandbox run ID returned from buildUberHeader
   * @param {String} sandboxRunID - Run ID returned from createSandboxRun
   * @param {String} sandboxRunID - Sample Driver ID returned from getSandboxRunAndDriverIDs
   * @param {String} newDriverState - Name of next ride state ('ACCEPT', 'ARRIVED', 'BEGIN_TRIP', or 'DROPOFF')
   * @returns {Object} - Stub containing success boolean and any data returned from API call
   */
  changeUberSandboxDriverState: async (uberHeader, sandboxRunID, uberDriverId, newDriverState) => {
    let driverStateStub = {
      success: false,
      data: '',
    };
    try {
      var data = JSON.stringify({
        run_id: sandboxRunID,
        driver_id: uberDriverId,
        driver_state: newDriverState,
      });

      var config = {
        method: 'post',
        url: 'https://sandbox-api.uber.com/v1/guests/sandbox/driver-state',
        headers: uberHeader,
        data: data,
      };

      await axios(config).then(res => {
        if (res && res.data) {
          driverStateStub = {
            success: true,
            data: res.data,
          };
        } else throw new Error(`Failed to change Uber Sandbox Driver State to ${newDriverState}`, res);
      });
    } catch (err) {
      throw err;
    }
    return driverStateStub;
  },

  // -------------------------------------- AUTO RIDE FUNCTIONS --------------------------------------

  /**
   * Sets ride_type on ride move to chosen ride service
   *
   * @param {Number} rideMoveID - Ride move ID from Hasura event
   * @param {String} newRideService - Ride_type selected from "Estimating" state
   * @returns {Object} - A graphql mutation response object
   */

  updateRideType: async (rideMoveId, newRideService) => {
    if (!rideMoveId) {
      throw new Error('no ride move ID entered');
    }
    if (!newRideService) {
      throw new Error('no ride service entered');
    }

    try {
      const res = await _parent.gql.mutation(
        `
          mutation update_move_ride_type($rideMoveId: bigint!, $rideType: String!) {
            update_moves(where: { id: { _eq: $rideMoveId } }, _set: { ride_type: $rideType, updatedat: "now()" }) {
              affected_rows
              returning {
                id
                ride_type
              }
            }
          }
        `,
        { rideMoveId: rideMoveId, rideType: newRideService }
      );

      if (!res.success) throw new Error(`No response from updateRideType`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },
  /**
   * Sets decision_reason on ride record to reasoning for chosen ride service
   *
   * @param {Number} rideID - ID of ride record
   * @param {String} decisionReason - Reason for selecting ride_type (admin override, customer config, auto decision based on cheaper cost estimate, or no_decision if admin initially selected)
   * @returns {Object} - A graphql mutation response object
   */

  updateDecisionReason: async (rideId, decisionReason) => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }
    if (!decisionReason) {
      throw new Error('no decision reason entered');
    }

    try {
      const res = await _parent.gql.mutation(
        `
          mutation update_ride_status($rideId: bigint!, $decisionReason: String!) {
            update_lyftrides(where: {id: {_eq: $rideId}}, _set: {decision_reason: $decisionReason, updatedat: "now()"}) {
              affected_rows
              returning {
                id
                decision_reason
                updatedat
              }
            }
          }
        `,
        { rideId: rideId, decisionReason: decisionReason }
      );

      if (!res.success) throw new Error(`No response from updateDecisionReason`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },

  /**
   * Clears decision_reason on ride record for repeat auto ride calls
   *
   * @param {Number} rideID - ID of ride record
   * @returns {Object} - A graphql mutation response object
   */

  setDecisionReasonNull: async rideId => {
    if (!rideId) {
      throw new Error('no ride ID entered');
    }
    if (typeof rideId !== 'number') {
      throw new Error('ride ID must be an integer');
    }

    try {
      const res = await _parent.gql.mutation(
        `
          mutation update_ride_status($rideId: bigint!) {
            update_lyftrides(where: {id: {_eq: $rideId}}, _set: {decision_reason: null, updatedat: "now()"}) {
              affected_rows
              returning {
                id
                updatedat
              }
            }
          }
        `,
        { rideId: rideId }
      );

      if (!res.success) throw new Error(`No response from setDecisionReasonNull`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },

  handlePendingState: async (rideMove, rideRecord, customerID, hailService) => {
    console.log('----- PENDING STATE -----');
    try {
      if (hailService === 'auto') {
        console.log('Auto Ride, Checking Customer Config...');
        let { ride_type_override } = await _parent.customers.getConfig(customerID);

        if (!ride_type_override) {
          // Move to "Estimating" State if no vendor preference is found in custimer config

          console.log("No Customer Ride Type Override,  Moving to 'Estimating' state");
          await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.ESTIMATING.code);
          return;
        } else {
          console.log("Customer Ride Type Override Found, Moving to 'Calling' state");
          // Change Ride Type to Customer Override and update Decision Reason
          await ridehail.updateRideType(rideMove.id, ride_type_override);
          await ridehail.updateDecisionReason(
            rideRecord.id,
            `Customer's preferred ride service set to ${
              ride_type_override.charAt(0).toUpperCase() + ride_type_override.slice(1)
            }`
          );
          // Set Ride status to "Calling"
          await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.CALLING.code);
          return;
        }
      } else {
        console.log("Ride Service Already Chosen, Moving to 'Calling' state");
        // Clear Decision Reason
        await ridehail.setDecisionReasonNull(rideRecord.id);
        // Set Ride status to "Calling"
        await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.CALLING.code);
        return;
      }
    } catch (e) {
      console.log("Pending State Error, setting Ride status to 'Failed':", e);
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.FAILED.code);
      return;
    }
  },

  buildEstimateAttempts: async rideId => {
    // Create UUID
    let estimateId = uuidv4();

    // Create 2 attempts and set UUID as estimate_id on each
    let lyftAttempt = await ridehail.createAttempt(
      rideId,
      ridehail.rideTypes.LYFT.code,
      ridehail.attemptStatuses.ESTIMATING.code,
      estimateId
    );
    let lyftAttemptID = lyftAttempt.data[0].id;

    let uberAttempt = await ridehail.createAttempt(
      rideId,
      ridehail.rideTypes.UBER.code,
      ridehail.attemptStatuses.ESTIMATING.code,
      estimateId
    );
    let uberAttemptID = uberAttempt.data[0].id;

    // Set UUID as active_estimate_attempts on ride
    let activeEstimateReturn = await ridehail.updateActiveEstimateAttempts(rideId, estimateId);
    console.log('activeEstimateReturn', activeEstimateReturn);

    return { lyftAttemptID, uberAttemptID };
  },

  handleEstimatingState: async (rideMove, rideRecord, lyftHeader, uberHeader) => {
    console.log('----- ESTIMATING STATE -----');
    let lyftRideCostEstimate, lyftUpdateObject, uberRideCostEstimate, uberUpdateObject;
    try {
      // Create Attempt Records for each ride vendor in estimating status
      let { lyftAttemptID, uberAttemptID } = await ridehail.buildEstimateAttempts(rideRecord.id);

      // Get price estimates for the current ride
      console.log('Getting Estimates with headers:', lyftHeader, uberHeader);
      lyftRideCostEstimate = await ridehail.estimateLyft(rideMove, lyftHeader);
      console.log('Retrieved Lyft Ride Estimate:', lyftRideCostEstimate);
      uberRideCostEstimate = await ridehail.estimateUber(rideMove, uberHeader);
      console.log('Retrieved Uber Ride Estimate:', uberRideCostEstimate);

      // Build data objects to save estimate data to estimate ride attempts
      lyftUpdateObject = await ridehail.buildLyftAttemptUpdateObject(rideMove, lyftAttemptID, lyftRideCostEstimate);
      console.log('Lyft Update Object:', lyftUpdateObject);
      uberUpdateObject = await ridehail.buildUberAttemptUpdateObject(rideMove, uberAttemptID, uberRideCostEstimate);
      console.log('Uber Update Object:', uberUpdateObject);

      // Save data objects with the price estimate data to Hasura
      let lyftUpdateResponse = await ridehail.updateRideAttempt(lyftUpdateObject);
      console.log(`Lyft Ride Attempt #${lyftAttemptID} Updated:`, lyftUpdateResponse);
      let uberUpdateResponse = await ridehail.updateRideAttempt(uberUpdateObject);
      console.log(`Uber Ride Attempt #${uberAttemptID} Updated:`, uberUpdateResponse);

      // Check ride_type for manual override
      if (rideMove.ride_type !== ridehail.rideTypes.AUTO.code) {
        console.log(`Override Found: Calling ${rideMove.ride_type} ride`);
        // Update decision reason to "admin"
        await ridehail.updateDecisionReason(
          rideRecord.id,
          `Auto Ride Decision overridden by Admin selection: ${
            rideMove.ride_type.charAt(0).toUpperCase() + rideMove.ride_type.slice(1)
          }`
        );
        // Update ride status - Move to "Calling" State
        await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.CALLING.code);
        // Set active estimate attempts to Null - New ride attempt will be created on call
        await ridehail.setActiveEstimateAttemptsToNull(_parent.utilities.getPropValue(rideMove, `ride.id`));
        return;
      }

      // Update ride status - Move to "Deciding" State
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.DECIDING.code);
    } catch (e) {
      console.log("Estimating State Error, setting Ride status to 'Failed' and Active Estimate Attempts to Null:", e);
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.FAILED.code);
      await ridehail.setActiveEstimateAttemptsToNull(_parent.utilities.getPropValue(rideMove, `ride.id`));
      return;
    }
  },

  handleDecidingState: async (rideMove, rideRecord, allAttempts, estimateAttempts) => {
    console.log('----- DECIDING STATE -----');
    let chosenRideService, costReasoning, winningEstimateID, losingEstimateID;

    let { estimated_ride_cost: estimatedUberPrice, id: uberEstimateID } =
      estimateAttempts.find(attempt => attempt.ride_type === ridehail.rideTypes.UBER.code) || {};
    let { estimated_ride_cost: estimatedLyftPrice, id: lyftEstimateID } =
      estimateAttempts.find(attempt => attempt.ride_type === ridehail.rideTypes.LYFT.code) || {};

    console.log(`Active Uber Estimate: #${uberEstimateID}, $${estimatedUberPrice}`);
    console.log(`Active Lyft Estimate: #${lyftEstimateID}, $${estimatedLyftPrice}`);

    try {
      // Compare the estimated costs and select the cheaper ride service, default to Lyft if prices are equal
      // Set costReasoning, chosenRideService, winningEstimateID, and losingEstimateID variables accordingly
      if (estimatedLyftPrice && estimatedUberPrice) {
        if (estimatedLyftPrice < estimatedUberPrice) {
          costReasoning = `Lyft (#${lyftEstimateID}): $${estimatedLyftPrice.toFixed(
            2
          )} < Uber (#${uberEstimateID}): $${estimatedUberPrice.toFixed(2)}`;
          chosenRideService = ridehail.rideTypes.LYFT.code;
          winningEstimateID = lyftEstimateID;
          losingEstimateID = uberEstimateID;

          console.log(costReasoning);
        } else if (estimatedLyftPrice > estimatedUberPrice) {
          costReasoning = `Uber (#${uberEstimateID}): $${estimatedUberPrice.toFixed(
            2
          )} < Lyft (#${lyftEstimateID}): $${estimatedLyftPrice.toFixed(2)}`;
          chosenRideService = ridehail.rideTypes.UBER.code;
          winningEstimateID = uberEstimateID;
          losingEstimateID = lyftEstimateID;

          console.log(costReasoning);
        } else {
          // Default to Lyft if equal
          costReasoning = `Lyft (#${lyftEstimateID}): $${estimatedLyftPrice.toFixed(
            2
          )} = Uber (#${uberEstimateID}): $${estimatedUberPrice.toFixed(2)}`;
          chosenRideService = ridehail.rideTypes.LYFT.code;
          winningEstimateID = lyftEstimateID;
          losingEstimateID = uberEstimateID;

          console.log(costReasoning);
        }
        // Default to Lyft if either estimate is not found
      } else {
        costReasoning = `Error retrieving estimated ride costs, defaulting to Lyft ride`;
        chosenRideService = ridehail.rideTypes.LYFT.code;
        winningEstimateID = lyftEstimateID;
        losingEstimateID = uberEstimateID;

        console.log(costReasoning);
      }
      // Check ride_type for manual override
      if (rideMove.ride_type !== ridehail.rideTypes.AUTO.code) {
        console.log(`Override Found: Calling ${rideMove.ride_type} ride`);
        // Update decision reason to "admin"
        await ridehail.updateDecisionReason(
          rideRecord.id,
          `Auto Ride Decision overridden by Admin selection: ${
            rideMove.ride_type.charAt(0).toUpperCase() + rideMove.ride_type.slice(1)
          }`
        );
        // Set Ride status to "Calling"
        await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.CALLING.code);
        // Set active estimate attempts to Null - New ride attempt will be created on call
        await ridehail.setActiveEstimateAttemptsToNull(_parent.utilities.getPropValue(rideMove, `ride.id`));
        return;
      }

      // Update Ride Type to chosen ride service
      console.log(`Chosen Ride Service:`, chosenRideService);
      await ridehail.updateRideType(rideMove.id, chosenRideService);

      // Update Winning Attempt status to Chosen and Losing Attempt status to Not Chosen
      await ridehail.updateAttemptStatus(winningEstimateID, ridehail.attemptStatuses.CHOSEN.code);
      await ridehail.updateAttemptStatus(losingEstimateID, ridehail.attemptStatuses.NOTCHOSEN.code);

      // Update decision reason to "auto"
      await ridehail.updateDecisionReason(
        rideRecord.id,
        `Auto Ride Decision: ${
          chosenRideService.charAt(0).toUpperCase() + chosenRideService.slice(1)
        } ride selected, ${costReasoning}`
      );
      // Set Ride status to "Calling"
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.CALLING.code);
    } catch (e) {
      console.log("Deciding State Error, setting Ride status to 'Failed' and Active Estimate Attempts to Null:", e);
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.FAILED.code);
      await ridehail.setActiveEstimateAttemptsToNull(_parent.utilities.getPropValue(rideMove, `ride.id`));
      return;
    }
  },

  handleCallingState: async (rideMove, rideRecord, allAttempts, estimateAttempts, request, hailService) => {
    console.log('----- CALLING STATE -----');
    let attemptID, driverFirstName, driverLastName, attemptResponse;

    try {
      // Check for Active Attempt field on Ride Record
      if (rideRecord && rideRecord.activeAttempt && rideRecord.activeAttempt.status !== 'canceled') {
        console.log('This ride currently has an active attempt.');
        return;
      } else {
        console.log('No active attempt found');
      }

      // Check for Active Estimate Attempts on Ride Record
      if (rideRecord.active_estimate_attempts) {
        // Use winning Attempt Record if Active (find attempt with matching Estimate ID in Chosen status)
        let { id: chosenAttemptID } =
          estimateAttempts.find(attempt => attempt.status === ridehail.attemptStatuses.CHOSEN.code) || {};
        attemptID = chosenAttemptID;
        console.log(`Found Winning Ride Estimate: #${attemptID}`);

        // Set Winning Attempt Record to awaitingResponse status to begin ride call
        await ridehail.updateAttemptStatus(attemptID, ridehail.attemptStatuses.AWAITINGRESPONSE.code);
      } else {
        // No Active Attempt or Active Estimate Attempts - Create new Attempt Record in awaitingResponse status
        attemptResponse = await ridehail.createAttempt(
          rideRecord.id,
          hailService,
          ridehail.attemptStatuses.AWAITINGRESPONSE.code
        );
        attemptID = attemptResponse.data[0].id;
      }
      // Set new/winning Attempt as Active (Updating Ride Record with Active Attempt ID)
      await ridehail.updateActiveAttempt(rideMove.id, attemptID);
      // Update rideMove object with new Active Attempt ID (to be referenced by updateRideFailure in case of failure)
      rideMove.ride.active_attempt = attemptID;
    } catch (e) {
      console.log("Calling State Error: setting Ride status to 'Failed' and Active Estimate Attempts to Null:", e);
      await ridehail.updateRideStatus(rideRecord.id, ridehail.rideStatuses.FAILED.code);
      await ridehail.setActiveEstimateAttemptsToNull(_parent.utilities.getPropValue(rideMove, `ride.id`));
      await ridehail.updateRideMoveFailure(_parent.utilities.getPropValue(rideMove, `id`));
      return;
    }

    try {
      // Get Driver Phone (from Hasura Request or Tookan) and Pickup Coord overrides (from Request if provided from returns)
      let overrides = await ridehail.rideMoveOverrides(request, rideMove);

      // Format Driver Phone ('+##########') and override Ride Move phone number
      let formattedNumber = await ridehail.formatDriverPhone(overrides.driver_phone);
      rideMove.driver_phone = formattedNumber;

      // Override Ride Move coords
      if (overrides.pickupLatitude || overrides.pickupLongitude) {
        rideMove.lane.pickup.latitude = overrides.pickupLatitude ? overrides.pickupLatitude : null;
        rideMove.lane.pickup.longitude = overrides.pickupLongitude ? overrides.pickupLongitude : null;
        // 10/2022 - Lyft now requires an address string with every request, so we can no longer null out the addresses here
        // rideMove.lane.pickup.address = null;
        // rideMove.lane.delivery.address = null;
      }

      // Get driver name object from Ride Move driver name
      let driverName = await ridehail.getDriverName(rideMove);

      driverFirstName = driverName.first;
      driverLastName = driverName.last;

      // Check if driver name is valid
      if (!ridehail.validateDriverName(driverFirstName, driverLastName)) {
        throw new Error("Could not parse driver's name to be accepted by rideshare APIs");
      }
    } catch (e) {
      console.log(
        'Calling State Error: Setting Ride and Attempt statuses to Failed, and Active Estimate Attempts to Null',
        e
      );
      await ridehail.updateRideFailure(
        _parent.utilities.getPropValue(rideMove, `id`),
        _parent.utilities.getPropValue(rideMove, `ride.active_attempt`)
      );

      return;
    }
    return { attemptID, driverFirstName, driverLastName };
  },

  // -------------------------------------- RIDE CANCEL FUNCTIONS --------------------------------------

  /**
   * Sets attempt record status to "failed" after canceling a ride
   *
   * @param {String} rideshareId - Ride service ID from Ride Attempt
   * @returns {Object} - A graphql mutation response object
   */

  cancelAttempt: async rideshareId => {
    if (!rideshareId) {
      throw new Error('No rideshare ID entered!');
    }

    try {
      const res = await _parent.gql.mutation(
        `mutation cancelAttempt($rideshareId: String!) {
              update_lyftrideattempts(
                where: {rideshare_id: {_eq: $rideshareId}},
                _set: {
                  status: "canceled"
                  updatedat: "now()"
              }) {
                affected_rows
              }
            }`,
        { rideshareId: rideshareId }
      );

      if (!res || !res.success) throw new Error(`No response from mutation!`, res);

      return res;
    } catch (err) {
      throw err;
    }
  },

  // -------------------------------------- RIDE WEBHOOK FUNCTIONS --------------------------------------

  /**
   * Updates Ride Move and Attempt after Webhook events
   *
   * @param {Object} attemptObj - Webhook data to be added to Ride Attempt
   * @param {Object} moveObj - Webhook data to be added to Ride Move(s)
   * @param {String} rideshareId - Rideshare Service-specific Ride ID from Webhook event
   */
  updateTripSystem: async (attemptObj, moveObj, rideshareId) => {
    if (!rideshareId) {
      throw new Error('No rideshare ID entered!');
    }

    // Query to find any other ride moves in the group (this is extremely messy and shouldnt be this difficult to do)
    const RIDE_MOVE_QUERY = gql`
      query sdk_ridehail_getRideGroup($rideshareId: String!) {
        lyftrideattempts(where: { rideshare_id: { _eq: $rideshareId } }) {
          ride {
            ride_move_id
            ride_move {
              lane {
                id
              }
            }
            driversInRidegroupByRideMoveId {
              ridegroup {
                move {
                  childMoves(where: { active: { _eq: 1 }, move_type: { _eq: "ride" } }) {
                    id
                    lane {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    // Update the attempt and ride move(s)
    const RIDE_MUTATION = gql`
      mutation sdk_ridehail_updateAttemptAndMove($rideshareId: String!, $rideMoveIds: [bigint!]!) {
        update_lyftrideattempts(
          where: {rideshare_id: {_eq: $rideshareId}}
          _set: ${attemptObj}
        ) {
          affected_rows
          returning {
            id
            rideshare_id
            status
          }
        }

        update_moves(
          where: {id: {_in: $rideMoveIds}}
          _set: ${moveObj}
        ) {
          affected_rows
          returning {
            id
            status
          }
        }
      }
    `;

    try {
      // Find all rides in the group
      const groupRes = await _parent.gql.query(RIDE_MOVE_QUERY, { rideshareId });
      if (!groupRes || !groupRes.success || !groupRes.data || !groupRes.data.length)
        throw new Error(`Invalid response from querying ride group!`);
      const groupResData = groupRes.data[0];

      // Collect all the grouped ride move IDs (this is the complex part)
      let groupRideMoveIds = [];

      // Dig into ridegroups to find related ride move IDs
      // 01/08/2025 Eric - Added new check for only collecting ride moves that have the same lane ID as the master ride move
      //   This is a TEMPORARY fix to ensure we only update rides that meet the required criteria until we have time to implement a better solution
      const ridegroups = getPropValue(groupResData, 'ride.driversInRidegroupByRideMoveId.ridegroup');
      if (ridegroups && ridegroups.length) {
        const driveMoves = ridegroups.map(rg => rg.move);
        if (driveMoves && driveMoves.length) {
          const childMovesArrays = driveMoves.map(dm => dm.childMoves);
          if (childMovesArrays && childMovesArrays.length) {
            let childRideMoves = childMovesArrays.map(cma => {
              if (cma && cma.length) return cma;
            });

            if (childRideMoves && childRideMoves.length) childRideMoves = childRideMoves.flat();

            groupRideMoveIds = childRideMoves
              .filter(gcm => gcm.lane.id === getPropValue(groupResData, 'ride.ride_move.lane.id'))
              .map(gcm => gcm.id);
          }
        }
      }

      // Add the master ride move ID if its not a ridegroup (this value should always be present)
      const masterRideMoveId = groupResData.ride.ride_move_id;
      if (!groupRideMoveIds.includes(masterRideMoveId)) groupRideMoveIds.push(masterRideMoveId);

      // Update the attempt and ride move(s)
      const res = await _parent.gql.mutation(RIDE_MUTATION, { rideshareId, rideMoveIds: groupRideMoveIds });
      if (!res || !res.success) throw new Error(`Invalid response from attempting to update ride(s)!`);

      // Return the response
      return res;
    } catch (err) {
      throw new Error(`Failed to update attempt and ride move(s)!\n${JSON.stringify(err, null, 2)}`, { cause: err });
    }
  },

  /**
   * Packages up the necessary into to be able to update a ride group's distributed accessorials when the composition of the ridegroup changes
   * Returns an array of objects including the drive move ID, distributed ride and surge costs,
   * and the existing ride and surge accessorial IDs to be updated for each move in the group
   * @param {number} cost - Total cost of the ride
   * @param {String} groupId - UUID of the ride group
   * @returns {Array} - Array of drive move IDs
   */

  getRidegroupCostsToUpdate: async (groupId = null, cost = null) => {
    if (!groupId) {
      throw new Error('No group ID entered!');
    }

    if (!cost) {
      throw new Error('No cost entered!');
    }

    const GET_RIDEGROUP_DRIVES = gql`
      query sdk_ridehail_getRidegroupDrives($groupId: uuid!) {
        ridegroups(where: { group_id: { _eq: $groupId } }) {
          drive_move_id
          move {
            accessorials(where: { code: { _in: ["ride", "surge"] } }, order_by: { createdat: asc }) {
              id
              code
            }
            lane {
              estimated_rideshare_return_cost
            }
          }
        }
      }
    `;

    try {
      const res = await _parent.gql.query(GET_RIDEGROUP_DRIVES, { groupId: groupId });
      let numberOfDrives = res && res.data && res.data.length > 0 ? res.data.length : 0;
      let rideGroupDrives = res && res.data && res.data.length > 0 ? res.data : [];
      let laneCostEstimate =
        rideGroupDrives.length > 0 &&
        rideGroupDrives[0].move &&
        rideGroupDrives[0].move.lane &&
        rideGroupDrives[0].move.lane.estimated_rideshare_return_cost
          ? rideGroupDrives[0].move.lane.estimated_rideshare_return_cost
          : null;
      let groupedDrives = [];

      if (cost && numberOfDrives) {
        let basicCostPerDrive = cost / numberOfDrives;
        let roundedDownCost = Math.floor(basicCostPerDrive * 100) / 100; // Rounds down to nearest penny
        let remainingAmount = Math.round((cost - roundedDownCost * numberOfDrives) * 100); // Remaining amount in cents

        rideGroupDrives.forEach((rideGroupDrive, index) => {
          let finalCost = roundedDownCost;
          if (index < remainingAmount) {
            finalCost += 0.01;
          }

          let rideAccessorial = null;
          let surgeAccessorial = null;

          if (rideGroupDrive.move && rideGroupDrive.move.accessorials && rideGroupDrive.move.accessorials.length > 0) {
            const rideAccessorials = rideGroupDrive.move.accessorials.filter(
              accessorial => accessorial.code === 'ride'
            );
            const surgeAccessorials = rideGroupDrive.move.accessorials.filter(
              accessorial => accessorial.code === 'surge'
            );
            rideAccessorial = rideAccessorials.length ? rideAccessorials[0].id : null;
            surgeAccessorial = surgeAccessorials.length ? surgeAccessorials[0].id : null;
          }

          let surgeCost = 0;
          if (surgeAccessorial && laneCostEstimate) {
            const costDelta = Math.round((cost - laneCostEstimate) * 100) / 100;
            let basicSurgeCostPerDrive = costDelta / numberOfDrives;
            let roundedDownSurgeCost = Math.floor(basicSurgeCostPerDrive * 100) / 100;
            let remainingSurgeAmount = Math.round((costDelta - roundedDownSurgeCost * numberOfDrives) * 100);

            surgeCost = roundedDownSurgeCost;
            if (index < remainingSurgeAmount) {
              surgeCost += 0.01;
            }
          }

          groupedDrives.push({
            driveMoveId: rideGroupDrive.drive_move_id,
            cost: parseFloat(finalCost.toFixed(2)),
            surgeCost: parseFloat(surgeCost.toFixed(2)),
            rideAccessorial: rideAccessorial,
            surgeAccessorial: surgeAccessorial,
          });
        });
      }

      return groupedDrives;
    } catch (error) {
      throw new Error(`Error fetching ride group drives: ${error.message}`);
    }
  },

  /**
   * Gets associated drive moves for a ride group
   * @param {String} rideshareId - Rideshare Service-specific Ride ID from Webhook event
   * @returns {Array} - Array of drive move IDs
   */
  getRidegroupDrives: async (rideshareId, cost) => {
    if (!rideshareId) {
      throw new Error('No rideshare ID entered!');
    }

    const GET_RIDEGROUP_DRIVES = gql`
      query sdk_ridehail_getRidegroupDrives($rideshareId: String!) {
        lyftrideattempts(where: { rideshare_id: { _eq: $rideshareId } }) {
          ride_cost
          ride {
            attempts {
              ride_id
            }
            ride_move {
              parent_move_id
              parentMove {
                config
              }
            }
            driversInRidegroupByRideMoveId {
              ridegroup {
                drive_move_id
              }
            }
          }
        }
      }
    `;

    const res = await _parent.gql.query(GET_RIDEGROUP_DRIVES, { rideshareId: rideshareId });
    let parentDriveId = 0;
    let groupedDrives = [];
    let totalRideCost = 0;
    let isTriangleMove = false;
    let rideId = 0;

    if (res && res.data && res.data.length > 0) {
      let data = res.data[0];
      let ride = res.data[0].ride;
      if (ride && ride.ride_move && ride.ride_move.parent_move_id) parentDriveId = ride.ride_move.parent_move_id;
      if (
        ride &&
        ride.ride_move &&
        ride.ride_move.parentMove &&
        ride.ride_move.parentMove.config &&
        ride.ride_move.parentMove.config.triangle_move &&
        ride.ride_move.parentMove.config.triangle_move === true
      )
        isTriangleMove = true;
      if (ride && ride.attempts && ride.attempts.length > 0) rideId = ride.attempts[0].ride_id;
      if (data.ride_cost) totalRideCost = data.ride_cost;
      if (
        ride &&
        ride.driversInRidegroupByRideMoveId &&
        ride.driversInRidegroupByRideMoveId.ridegroup &&
        ride.driversInRidegroupByRideMoveId.ridegroup.length > 0
      ) {
        const rideGroupDrives = ride.driversInRidegroupByRideMoveId.ridegroup;
        let numberOfDrives = rideGroupDrives.length;

        // If a cost param is passed in, we'll calculate the cost per drive based on that (in the event of a surge accessorial, for example)
        // If not, we'll calculate the cost per drive based on the total ride cost divided by the number of drives
        let costToUse = cost || totalRideCost;
        let basicCostPerDrive = costToUse / numberOfDrives;
        let roundedDownCost = Math.floor(basicCostPerDrive * 100) / 100; // Rounds down to nearest penny
        let remainingAmount = Math.round((costToUse - roundedDownCost * numberOfDrives) * 100); // Remaining amount in cents

        // Assign costs to each drive, distributing the remaining amount
        rideGroupDrives.forEach((rideGroupDrive, index) => {
          let finalCost = roundedDownCost;
          if (index < remainingAmount) {
            // Distribute remaining amount
            finalCost += 0.01; // Add an extra penny to some drives to account for the remainder
          }
          groupedDrives.push({ driveMoveId: rideGroupDrive.drive_move_id, cost: parseFloat(finalCost.toFixed(2)) });
        });
      }
    }
    return {
      totalRideCost: totalRideCost,
      parentDriveId: parentDriveId,
      parentDrive: { driveMoveId: parentDriveId, rideId: rideId, isTriangleMove: isTriangleMove },
      groupedDrives: groupedDrives,
    };
  },

  /**
   * Gets active lyftride attempt's actual cost from rideshare group id
   * @param {String} groupId - ridegroup id
   */

  getLyftRideCostForGroup: async groupId => {
    if (!groupId) {
      throw new Error('No group ID entered!');
    }

    try {
      const GET_ACTIVE_LYFT_RIDE_ATTEMPT_COST_FOR_GROUP = gql`
        query sdk_ridehail_getRidegroupLyftrideAttempt($groupId: uuid!) {
          ridegroups(
            where: {
              group_id: { _eq: $groupId }
              ready_at_time: { _is_null: false }
              move: { childMoves: { ride: { activeAttempt: { ride_cost: { _is_null: false } } } } }
            }
            order_by: { ready_at_time: asc }
          ) {
            move {
              childMoves {
                ride {
                  activeAttempt {
                    ride_cost
                  }
                }
              }
            }
          }
        }
      `;

      let rideCost = 0;
      const res = await _parent.gql.query(GET_ACTIVE_LYFT_RIDE_ATTEMPT_COST_FOR_GROUP, { groupId: groupId });
      if (res && res.data && res.data.length > 0) {
        if (res.data[0].move && res.data[0].move.childMoves && res.data[0].move.childMoves.length > 0) {
          rideCost = res.data[0].move.childMoves[0].ride.activeAttempt.ride_cost;
        }
      }

      return rideCost;
    } catch (err) {
      console.log('Error getting active lyft ride attempt for group:', err);
      return null;
    }
  },
  /**
   * Finds parent move in order to redispatch ride after certain Webhook events
   * (currently unused in favor of drivers recalling rides using link)
   *
   * @param {String} move_id - Ride move ID found from handleRideCanceled function
   * @returns {Object} moveStub - Object containing parent move data
   */
  handleNewRideDispatch: async move_id => {
    let moveStub;
    console.log('Retrieving parent move data for id#', move_id);
    let FIND_PARENT_MOVE_QUERY = `
      query findMove($id: bigint!) {
        moves(where: {id: {_eq: $id}}) {
          delivery_stop_id
          moveByReturnRideId {
            driver_name
            delivery_stop_id
            lane {
              id
              pickup {
                name
                address
                latitude
                longitude
              }
              delivery {
                name
                address
                latitude
                longitude
              }
            }
          }
        }
      }
    `;
    let FIND_PARENT_MOVE_VARIABLES = { id: move_id };
    Object.assign(FIND_PARENT_MOVE_VARIABLES, { id: move_id });
    // Retrieve parent trigger move info from Hasura
    try {
      let moveStub;
      const res = await _parent.gql.mutation(FIND_PARENT_MOVE_QUERY, RIDE_VARIABLES);
      moveStub = {
        move: res.data.data.moves[0],
        driver_phone: request.event.passenger.phone_number,
      };
      console.log('Triggering lyftDispatch function with event:', event);
      // send event to Netlify function here --------------------------------------------------------------------------------------------------------------------------------
    } catch (err) {
      throw new Error('Error retrieving parent move info:', err);
    }
    return moveStub;
  },

  /**
   * Fetch ride details to determine if a manual redispatch is required after Ride Cancelled Webhook events
   *
   * @param {String} rideshareID - Rideshare Service-specific Ride ID from Webhook event
   * @returns {Object} detailsStub - Object containing ride details for cancellation
   */
  fetchRideDetails: async rideshareID => {
    let detailsStub;
    let FIND_LYFT_RIDE_QUERY = `
      query findLyftRide($rideshare_id: String!) {
        lyftrideattempts(where: {rideshare_id: {_eq: $rideshare_id}}) {
          move_id
          status
          rideshare_id
          origin_location_id
          destination_location_id
        }
      }
      `;
    let FIND_LYFT_RIDE_VARIABLES = { rideshare_id: rideshareID };

    try {
      const res = await _parent.gql.mutation(FIND_LYFT_RIDE_QUERY, FIND_LYFT_RIDE_VARIABLES);

      detailsStub = res.data.data.lyftrideattempts[0];
    } catch (err) {
      throw new Error('Error retrieving Lyft ride info from Hasura:', err);
    }
    return detailsStub;
  },
};

module.exports = ridehail;
